import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import eventService from "../services/eventServices";

/**
 * Ensures list slices + CSmartTable always receive an array (API may wrap or error handlers may store non-arrays).
 */
export function normalizeListPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload == null) return [];
  if (typeof payload !== "object") return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.events)) return payload.events;
  if (Array.isArray(payload.list)) return payload.list;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.tickets)) return payload.tickets;
  if (Array.isArray(payload.orders)) return payload.orders;
  if (Array.isArray(payload.bookings)) return payload.bookings;
  return [];
}

/** eventId string, or { eventId, clubid } for club-scoped ticket / queue APIs */
function resolveEventScopedRequest(payload) {
  if (payload == null || payload === "") {
    return { id: null, params: {} };
  }
  if (typeof payload === "object" && !Array.isArray(payload)) {
    const id = payload.eventId ?? payload.id ?? payload._id ?? null;
    const params = {};
    const cid = payload.clubid;
    if (cid != null && cid !== "" && String(cid) !== "undefined") {
      params.clubid = String(cid);
    }
    return { id, params };
  }
  return { id: String(payload), params: {} };
}

function normalizeClubEventListPayload(res) {
  const body = res?.data !== undefined ? res.data : res;
  return normalizeListPayload(body);
}

export const currentEventListResponce = createAsyncThunk(
  "event/currentEvent",
  async (query, { rejectWithValue }) => {
    try {
      const res = await eventService.currentEvents(query || {});
      if (res == null) return [];
      return res?.data !== undefined ? res.data : res;
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.message || "Failed to load current events";
      return rejectWithValue(msg);
    }
  }
);

/** GET /event/list?clubid= — club staff offline sale */
export const clubStaffEventListResponce = createAsyncThunk(
  "event/clubStaffList",
  async (clubid, { rejectWithValue }) => {
    try {
      if (clubid == null || clubid === "") return [];
      const res = await eventService.eventListByClub(clubid);
      return normalizeClubEventListPayload(res);
    } catch (err) {
      const msg =
        typeof err === "string" ? err : err?.message || "Failed to load events";
      return rejectWithValue(msg);
    }
  }
);

export const pastEventListResponce = createAsyncThunk(
  "event/pastEvent",
  async (query, { rejectWithValue }) => {
    try {
      const res = await eventService.pastEvents(query || {});
      if (res == null) return [];
      return res?.data !== undefined ? res.data : res;
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.message || "Failed to load past events";
      return rejectWithValue(msg);
    }
  }
);
export const addEvent = createAsyncThunk("event/create", async (data) => {
  const res = await eventService.addEvent(data);
  return res;
});

export const soldOutEvent = createAsyncThunk("event/soldout", async (data) => {
  const res = await eventService.soldOutEvent(data);
  return { res, data };
});

export const updateEvent = createAsyncThunk("event/update", async (data) => {
  const res = await eventService.updateEvent(data);
  return res;
});

export const deleteEvent = createAsyncThunk("event/delete", async (data) => {
  const res = await eventService.deleteEvent(data);
  return res;
});

export const eventCancel = createAsyncThunk("event/cencel", async (data) => {
  const res = await eventService.cancelEvent(data);
  return { res, data };
});

export const eventTicketStatus = createAsyncThunk(
  "event/ticketstatus",
  async (payload) => {
    const { id, params } = resolveEventScopedRequest(payload);
    if (id == null || id === "") {
      return [];
    }
    const res = await eventService.ticketStatus(id, params);
    return res?.data;
  }
);

export const eventInQueueList = createAsyncThunk(
  "event/inQueue",
  async (payload) => {
    const { id, params } = resolveEventScopedRequest(payload);
    if (id == null || id === "") {
      return [];
    }
    const res = await eventService.inQueueUsers(id, params);
    return res?.data;
  }
);

const eventSlice = createSlice({
  name: "event",
  initialState: {
    currentEventListResponce: [],
    currentEventListError: "",
    pastEventListResponce: [],
    pastEventListError: "",
    addEvent: [],
    soldOutEvent: [],
    soldOutEventError: "",
    addEventError: "",
    updateEvent: [],
    updateEventError: "",
    deleteEventRes: [],
    deleteEventError: "",
    cancelEvent: [],
    cancelEventError: "",
    eventTicketStatusListResponce: [],
    eventInQueueListResponce: [],
  },
  extraReducers: (builder) => {
    // current event list
    builder
      .addCase(currentEventListResponce.pending, (state) => {
        state.loading = true;
      })
      .addCase(currentEventListResponce.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEventListResponce = normalizeListPayload(
          action.payload
        );
      })
      .addCase(currentEventListResponce.rejected, (state, action) => {
        state.loading = false;
        state.currentEventListError =
          action.payload ?? action.error?.message ?? action.error;
      });

    builder
      .addCase(clubStaffEventListResponce.pending, (state) => {
        state.loading = true;
      })
      .addCase(clubStaffEventListResponce.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEventListResponce = normalizeListPayload(action.payload);
      })
      .addCase(clubStaffEventListResponce.rejected, (state, action) => {
        state.loading = false;
        state.currentEventListError = action.payload ?? action?.error;
      });

    // past event list
    builder
      .addCase(pastEventListResponce.pending, (state) => {
        state.loading = true;
      })
      .addCase(pastEventListResponce.fulfilled, (state, action) => {
        state.loading = false;
        state.pastEventListResponce = normalizeListPayload(action.payload);
      })
      .addCase(pastEventListResponce.rejected, (state, action) => {
        state.loading = false;
        state.pastEventListError =
          action.payload ?? action.error?.message ?? action.error;
      });
    // AddEvent
    builder
      .addCase(addEvent.pending, (state) => {
        state.loading = true;
      })
      .addCase(addEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.addEvent = action.payload;
      })
      .addCase(addEvent.rejected, (state, action) => {
        state.loading = false;
        state.addEventError = action?.error;
      });
    // soldOut
    builder
      .addCase(soldOutEvent.pending, (state) => {
        state.loading = true;
      })
      .addCase(soldOutEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.soldOutEvent = action.payload?.res;
        const list = normalizeListPayload(state.currentEventListResponce);
        state.currentEventListResponce = list.map((event) => {
          if (event?._id === action?.payload?.data?.id) {
            return { ...event, soldout: action?.payload?.data?.status };
          }
          return event;
        });

        // state
      })
      .addCase(soldOutEvent.rejected, (state, action) => {
        state.loading = false;
        state.soldOutEventError = action?.error;
      });

    // Update Event
    builder
      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.updateEvent = action.payload;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.updateEventError = action?.error;
      });

    // delete Event

    builder
      .addCase(deleteEvent.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteEventRes = action.payload;
        //  state.currentEventListResponce = state.currentEventListResponce?.filter((item) => item?._id !== action?.payload?.data?._id)
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
        state.deleteEventError = action?.error;
      });

    // cancel Event
    builder
      .addCase(eventCancel.pending, (state) => {
        state.loading = true;
      })
      .addCase(eventCancel.fulfilled, (state, action) => {
        state.loading = false;
        state.cancelEvent = action.payload?.res;
        state.currentEventListResponce = state.currentEventListResponce.map(
          (event) => {
            if (event?._id === action?.payload?.data) {
              return { ...event, eventstatus: "Cancel" };
            }
            return event;
          }
        );

        // state
      })
      .addCase(eventCancel.rejected, (state, action) => {
        state.loading = false;
        state.cancelEventError = action?.error;
      });

    // event ticketStatus list
    builder
      .addCase(eventTicketStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(eventTicketStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.eventTicketStatusListResponce = normalizeListPayload(
          action.payload
        );
      })
      .addCase(eventTicketStatus.rejected, (state, action) => {
        state.loading = false;
        state.eventTicketStatusListResponce = [];
      });

    // event inQueue list
    builder
      .addCase(eventInQueueList.pending, (state) => {
        state.loading = true;
      })
      .addCase(eventInQueueList.fulfilled, (state, action) => {
        state.loading = false;
        state.eventInQueueListResponce = normalizeListPayload(
          action.payload
        );
      })
      .addCase(eventInQueueList.rejected, (state, action) => {
        state.loading = false;
        state.eventInQueueListResponce = [];
      });
  },
});

export default eventSlice.reducer;
